/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Drawer, Flex, Input, message, Spin, Tag, Typography } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import api from '../../utils/api';

const { Title, Text } = Typography;

interface Organization {
  name: string;
  account_id: string;
}

export interface UserProfileData {
  user_id: string;
  name: string;
  email: string;
  role: string;
  access_organizations: Organization[];
  not_access_organizations: Organization[];
}

interface UserProfileProps {
  open: boolean;
  user: UserProfileData | null;
  onClose: () => void;
  onUpdate?: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ open, user, onClose, onUpdate }) => {
  const [profileUser, setProfileUser] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [accessedOrgs, setAccessedOrgs] = useState<Organization[]>([]);
  const [notAccessedOrgs, setNotAccessedOrgs] = useState<Organization[]>([]);
  const [searchText, setSearchText] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!user || !open) {
      setProfileUser(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = user.user_id ? { user_id: user.user_id } : { email: user.email };
    api
      .get('/auth/user-details', params)
      .then((response: any) => {
        if (cancelled) return;
        const userData = response?.user ?? {};
        const fullUser: UserProfileData = {
          user_id: userData.user_id ?? user.user_id,
          name: userData.username ?? user.name,
          email: userData.email || user.email,
          role: userData.role ?? user.role,
          access_organizations: response?.access_organizations ?? userData.access_organizations ?? [],
          not_access_organizations: response?.not_access_organizations ?? userData.not_access_organizations ?? [],
        };
        setProfileUser(fullUser);
        setAccessedOrgs(fullUser.access_organizations);
        setNotAccessedOrgs(fullUser.not_access_organizations);
        setSearchText('');
      })
      .catch(() => {
        if (!cancelled) {
          setProfileUser(user);
          setAccessedOrgs(user.access_organizations ?? []);
          setNotAccessedOrgs(user.not_access_organizations ?? []);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, user?.user_id ?? user?.email]);

  const displayUser = profileUser ?? user;

  const hasChanges = useMemo(() => {
    if (!displayUser) return false;
    const origIds = (displayUser.access_organizations || []).map((o) => o.account_id).sort();
    const currentIds = accessedOrgs.map((o) => o.account_id).sort();
    return JSON.stringify(origIds) !== JSON.stringify(currentIds);
  }, [displayUser, accessedOrgs]);

  const handleToggleOrg = (org: Organization, grant: boolean) => {
    if (grant) {
      setAccessedOrgs((prev) => [...prev, org]);
      setNotAccessedOrgs((prev) => prev.filter((o) => o.account_id !== org.account_id));
    } else {
      setAccessedOrgs((prev) => prev.filter((o) => o.account_id !== org.account_id));
      setNotAccessedOrgs((prev) => [...prev, org]);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setAccessedOrgs([...accessedOrgs, ...notAccessedOrgs]);
      setNotAccessedOrgs([]);
    } else {
      setNotAccessedOrgs([...accessedOrgs, ...notAccessedOrgs]);
      setAccessedOrgs([]);
    }
  };

  const filteredAccessed = useMemo(() => {
    if (!searchText) return accessedOrgs;
    return accessedOrgs.filter((o) => o.name.toLowerCase().includes(searchText.toLowerCase()));
  }, [accessedOrgs, searchText]);

  const filteredNotAccessed = useMemo(() => {
    if (!searchText) return notAccessedOrgs;
    return notAccessedOrgs.filter((o) => o.name.toLowerCase().includes(searchText.toLowerCase()));
  }, [notAccessedOrgs, searchText]);

  const allOrgs = [...accessedOrgs, ...notAccessedOrgs];
  const isAllSelected = allOrgs.length > 0 && notAccessedOrgs.length === 0;

  const handleSave = async () => {
    if (!displayUser) return;
    setSaving(true);
    try {
      const origAccessedIds = new Set((displayUser.access_organizations || []).map((o) => o.account_id));
      const origNotAccessedIds = new Set((displayUser.not_access_organizations || []).map((o) => o.account_id));

      const newlyGranted = accessedOrgs.filter((o) => !origAccessedIds.has(o.account_id));
      const newlyRevoked = notAccessedOrgs.filter((o) => !origNotAccessedIds.has(o.account_id));

      await api.post('/auth/user-details/save', {
        email: displayUser.email || displayUser.name,
        access_organizations_ids: newlyGranted.map((o) => o.account_id),
        not_access_organizations_ids: newlyRevoked.map((o) => o.account_id),
      });
      message.success('User access updated successfully.');
      onUpdate?.();
      onClose();
    } catch (error: any) {
      message.error(typeof error === 'string' ? error : 'Failed to update user access.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Drawer
      title="User Profile"
      placement="right"
      width={480}
      open={open}
      onClose={onClose}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving} disabled={!hasChanges || loading}>
            Save Changes
          </Button>
        </Flex>
      }
    >
      {loading ? (
        <Flex justify="center" align="center" style={{ minHeight: 200 }}>
          <Spin size="large" />
        </Flex>
      ) : (
        <Flex vertical gap={24}>
          <Flex align="center" gap={12}>
            <Flex
              align="center"
              justify="center"
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#1677ff',
                color: '#fff',
                fontSize: 20,
              }}
            >
              <UserOutlined />
            </Flex>
            <Flex vertical>
              <Title level={5} style={{ margin: 0 }}>
                {displayUser?.name ?? user.name}
              </Title>
              <Text type="secondary">{displayUser?.email || user.email || displayUser?.name}</Text>
            </Flex>
          </Flex>

          <Flex vertical gap={12}>
            <Flex justify="space-between" align="center">
              <Text strong style={{ fontSize: 14 }}>
                Organization Access
              </Text>
              <Flex gap={12} align="center">
                <Checkbox checked={isAllSelected} onChange={(e) => handleSelectAll(e.target.checked)}>
                  <Text style={{ fontSize: 13 }}>Select All</Text>
                </Checkbox>
                <Button
                  type="link"
                  danger
                  onClick={() => handleSelectAll(false)}
                  style={{ padding: 0, fontSize: 13, height: 'auto' }}
                >
                  Clear All
                </Button>
              </Flex>
            </Flex>

            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Search organizations"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />

            {filteredAccessed.length > 0 && (
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Accessed Organizations ({accessedOrgs.length})
                </Text>
                <div
                  style={{
                    maxHeight: 200,
                    overflowY: 'auto',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    padding: 8,
                    background: '#fafafa',
                  }}
                >
                  {filteredAccessed.map((org) => (
                    <Flex
                      key={org.account_id}
                      align="center"
                      gap={8}
                      style={{
                        padding: '6px 8px',
                        marginBottom: 2,
                        background: '#fff',
                        borderRadius: 4,
                      }}
                    >
                      <Checkbox checked onChange={() => handleToggleOrg(org, false)} />
                      <Text style={{ flex: 1 }}>{org.name}</Text>
                      <Tag color="green" style={{ margin: 0 }}>
                        Accessed
                      </Tag>
                    </Flex>
                  ))}
                </div>
              </Flex>
            )}

            {filteredNotAccessed.length > 0 && (
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Available Organizations ({notAccessedOrgs.length})
                </Text>
                <div
                  style={{
                    maxHeight: 200,
                    overflowY: 'auto',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    padding: 8,
                    background: '#fafafa',
                  }}
                >
                  {filteredNotAccessed.map((org) => (
                    <Flex
                      key={org.account_id}
                      align="center"
                      gap={8}
                      style={{
                        padding: '6px 8px',
                        marginBottom: 2,
                        background: '#fff',
                        borderRadius: 4,
                      }}
                    >
                      <Checkbox checked={false} onChange={() => handleToggleOrg(org, true)} />
                      <Text style={{ flex: 1 }}>{org.name}</Text>
                    </Flex>
                  ))}
                </div>
              </Flex>
            )}

            {filteredAccessed.length === 0 && filteredNotAccessed.length === 0 && (
              <Text type="secondary" style={{ textAlign: 'center', padding: 16 }}>
                No organizations found
              </Text>
            )}
          </Flex>
        </Flex>
      )}
    </Drawer>
  );
};

export default UserProfile;
